import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { AppState } from "@Store/index";
import { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import wrapperHOC from "@Utils/wrapperHOC";
import config from "@Config/index";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import {
  updateInstallmentPlans,
  setInstallmentPlansFetching,
} from "@Pages/PropertyPage/data/propertyPageReducer";
import fetchInstallmentPlans from "../../data/fetchInstallmentPlans";
import useIsLanguage from "hooks/useIsLanguage";
import DesktopPaymentAndFees from "./DesktopPaymentAndFees";
import MobilePaymentAndFees from "./MobilePaymentAndFees";
import { displayAddOnCategoryTitle, FeeCategory } from "./types";

// Shared `|| []` fallback: a new array per selector call defeated shallowEqual on every dispatch.
const EMPTY: any[] = [];

const PaymentAndFees = ({ isDesktop = false }) => {
  const { inventoryId, regionId, installmentPlans, installmentPlansFetching, inventory } =
    useSelector(
      ({ propertyPageData }: AppState) => ({
        inventoryId: propertyPageData?.data?.id,
        regionId: propertyPageData?.data?.region_id,
        installmentPlans: propertyPageData?.installment_plans || EMPTY,
        installmentPlansFetching: propertyPageData?.installment_plans_fetching,
        inventory: propertyPageData?.data,
      }),
      shallowEqual,
    );

  const userLanguage = useIsLanguage();
  const propertyPageDispatch = useDispatch();

  const [selectedTab, setSelectedTab] = useState("all");
  const [selectedDuration, setSelectedDuration] = useState("All");
  const [selectedYear, setSelectedYear] = useState("All");
  const [isYearOpen, setIsYearOpen] = useState(false);
  const [isDurationOpen, setIsDurationOpen] = useState(false);
  const [forceCloseYear, setForceCloseYear] = useState(false);
  const [forceCloseDuration, setForceCloseDuration] = useState(false);

  // Exclude specific payment policies (same as PropertyPolicies component)
  const excludedPaymentPolices = new Set(["fully_refundable_holding_fee", "platform_fee"]);

  // Extract payment policies from inventory description, excluding specific ones
  const paymentPolicies =
    inventory?.description?.filter(
      (item: any) => item.name === "payment" && !excludedPaymentPolices.has(item.tag),
    ) || [];

  // PaymentAndFees fetches the installment plans and stores them in Redux
  // InstalmentSection will read from the same Redux store
  useEffect(() => {
    const fetchPlans = async () => {
      if (!inventoryId || !regionId) return;
      if (installmentPlansFetching || installmentPlans.length > 0) return;

      propertyPageDispatch(setInstallmentPlansFetching(true));

      try {
        const response = await fetchInstallmentPlans({
          id: inventoryId,
          regionId,
          selectedLang: userLanguage,
        });
        const rawPlans = response.data?.data?.result;
        propertyPageDispatch(updateInstallmentPlans({ installment_plans: rawPlans }));
      } catch (error) {
        propertyPageDispatch(setInstallmentPlansFetching(false));
      }
    };

    fetchPlans();
  }, [inventoryId, regionId, userLanguage, propertyPageDispatch]);

  // Extract unique durations and years for filters (same as InstalmentSection)
  // Filter out plans with 0 or missing duration — they shouldn't be shown
  const validPlans = installmentPlans.filter(
    (plan: any) => plan.duration && plan.duration > 0 && plan.duration_unit,
  );

  const durations = Array.from(
    new Set(
      validPlans
        .filter((plan: any) => plan.duration && plan.duration_unit)
        .map((plan: any) => `${plan.duration} ${plan.duration_unit}`),
    ),
  ).sort((a, b) => parseInt(a, 10) - parseInt(b, 10));

  const years = Array.from(
    new Set(validPlans.map((plan: any) => plan.academic_year).filter(Boolean)),
  ).sort((a, b) => {
    const [aStart] = a.replace("ay_", "").split("_").map(Number);
    const [bStart] = b.replace("ay_", "").split("_").map(Number);
    return aStart - bStart;
  });

  // Filter by year first
  const yearFilteredPlans = validPlans.filter((plan: any) =>
    selectedYear === "All" ? plan : plan.academic_year === selectedYear,
  );

  // Filter by duration
  const durationFilteredPlans = yearFilteredPlans.filter((plan: any) =>
    selectedDuration === "All"
      ? plan
      : `${plan.duration} ${plan.duration_unit}` === selectedDuration,
  );

  // Filter by guarantor type (tab)
  const getFilteredPlansByTab = () => {
    if (selectedTab === "all") {
      return durationFilteredPlans;
    }
    if (selectedTab === "without_guarantor") {
      return durationFilteredPlans.filter((plan: any) => plan.meta?.guarantor_required === "false");
    }
    if (selectedTab === "with_guarantor") {
      return durationFilteredPlans.filter(
        (plan: any) =>
          plan.meta?.guarantor_required &&
          plan.meta?.guarantor_required !== "false" &&
          plan.meta?.guarantor_required !== "information_unavailable",
      );
    }
    return durationFilteredPlans;
  };

  // Display filtered plans
  const displayPlans = getFilteredPlansByTab();

  // Calculate counts for each tab
  const allCount = durationFilteredPlans.length;
  const withGuarantorCount = durationFilteredPlans.filter(
    (plan: any) =>
      plan.meta?.guarantor_required &&
      plan.meta?.guarantor_required !== "false" &&
      plan.meta?.guarantor_required !== "information_unavailable",
  ).length;
  const withoutGuarantorCount = durationFilteredPlans.filter(
    (plan: any) => plan.meta?.guarantor_required === "false",
  ).length;

  // Check if we have installment plans to show
  const hasInstallmentPlans = validPlans && validPlans.length > 0;
  const hasPaymentPolicies = paymentPolicies && paymentPolicies.length > 0;

  // Structured fees (Required Fees / Add-On Fees) are gated behind a GrowthBook flag. China never
  // initializes GrowthBook, so OR in IS_CHINA_APP to keep the section visible there (matching the
  // convention used by other property-page sections). Installment plans and payment policies are
  // intentionally NOT gated — they render regardless of this flag.
  const isStructuredFeesEnabled =
    useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.STRUCTURED_FEES) || config.IS_CHINA_APP;

  const structuredFees = isStructuredFeesEnabled ? inventory?.structured_fees : null;
  const requiredFeeCategoryOrder = [
    "due_at_application",
    "post_booking_completion",
    "due_at_move_in",
    "recurring",
    "due_at_move_out",
  ];
  const requiredFeeCategories = [...(structuredFees?.required_fees?.categories || [])].sort(
    (a: { id?: string }, b: { id?: string }) => {
      const aIndex = requiredFeeCategoryOrder.indexOf(a.id ?? "");
      const bIndex = requiredFeeCategoryOrder.indexOf(b.id ?? "");
      return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
    },
  );
  const requiredFees = requiredFeeCategories.length
    ? {
        id: "required_fees",
        title: "Required Fees",
        categories: requiredFeeCategories,
      }
    : null;
  const addOnFees = structuredFees?.add_on_fees?.categories?.length
    ? {
        id: "add_on_fees",
        title: "Add-On Fees",
        categories: structuredFees.add_on_fees.categories.map((category: FeeCategory) => ({
          ...category,
          title: displayAddOnCategoryTitle(category),
        })),
      }
    : null;
  const feesConditionalNotes = structuredFees?.conditional_notes
    ? (Object.values(structuredFees.conditional_notes) as string[])
    : null;
  const hasRequiredFees = Boolean(requiredFees?.categories?.length);
  const hasAddOnFees = Boolean(addOnFees?.categories?.length);
  const hasStructuredFees = hasRequiredFees || hasAddOnFees;

  // Don't render if nothing to show
  if (!hasInstallmentPlans && !hasPaymentPolicies && !hasStructuredFees) {
    return null;
  }

  const commonProps = {
    selectedTab,
    setSelectedTab,
    selectedDuration,
    setSelectedDuration,
    selectedYear,
    setSelectedYear,
    durations,
    years,
    displayPlans,
    allCount,
    withGuarantorCount,
    withoutGuarantorCount,
    paymentPolicies, // Pass all payment policies instead of individual ones
    hasInstallmentPlans, // Pass this flag to child components
    requiredFees,
    addOnFees,
    // structured_fees.base_currency is optional and often absent; fall back to the inventory's
    // own pricing currency so fee amounts always render with a currency symbol.
    baseCurrency:
      structuredFees?.base_currency || inventory?.pricing?.currency || inventory?.currency,
    pricingDuration: inventory?.pricing?.duration,
    feesConditionalNotes,
    isYearOpen,
    setIsYearOpen,
    isDurationOpen,
    setIsDurationOpen,
    forceCloseYear,
    setForceCloseYear,
    forceCloseDuration,
    setForceCloseDuration,
  };

  return isDesktop ? (
    <DesktopPaymentAndFees {...commonProps} />
  ) : (
    <MobilePaymentAndFees {...commonProps} />
  );
};

export default wrapperHOC(PaymentAndFees, {
  componentName: "PaymentAndFees-PropertyPage",
  showForChina: true,
});
